import { ReactNode, useEffect, useMemo, useState } from 'react';

import { Loader } from '@gasbi/shared';
import OrganizationManager from '@gasbi/shared/common/utils/organizationManager';

import { useGetCurrentUserQuery } from '@/features/auth/authApi';
import { AuthContext } from '@/hooks/useAuth';
import http from '@/utils/http';

// Cookie (BFF) session bootstrap: one GET /api/user decides everything. With a
// live session it yields the user; without one the http interceptor navigates
// the browser to the BFF's /login, which challenges Identity Server and brings
// the user back to the page they asked for. Then one GET /api/session/organization
// (GSB-8274, PO 2026-10-25): the organization the user works in lives in the
// session, the landing opens it. Without the endpoint (404 while the API of
// GSB-8276 is not deployed) or without an organization the landing decides as before.
const Provider = ({ children }: { children: ReactNode }) => {
  const { data: user } = useGetCurrentUserQuery();
  const [isOrganizationReady, setIsOrganizationReady] = useState(false);

  useEffect(() => {
    if (!user) return;

    const onSettled = () => setIsOrganizationReady(true);
    OrganizationManager.initialize({ http }).then(onSettled, onSettled);
  }, [user]);

  const value = useMemo(
    () =>
      user && {
        user,
        logout: () => {
          window.location.assign('/api/logout?returnUrl=/');
        },
      },
    [user],
  );

  if (!value || !isOrganizationReady) {
    return <Loader />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default Provider;
